import React, { useEffect, useState, useRef } from "react";
import { IoIosArrowForward, IoMdClose } from "react-icons/io";
import { RxCross2 } from "react-icons/rx";
import {
  FaFileUpload,
  FaSpinner,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { FaFile } from "react-icons/fa6";
import { DataState } from "../context/DataProvider";
import useDrivePicker from "react-google-drive-picker";
import { google_client_id, google_api_key } from "../utils/keys";
import toast from "react-hot-toast";
import {
  uploadFilesDataSource,
  deleteFileDataSource,
  uploadFilesFromDriveDataSource,
  downloadFileDataSource,
} from "../services/api";
import SelectDataSourceModal from "./SelectDataSourceModal";
import SelectDataSource from "./SelectDataSource";
import { FiDownload } from "react-icons/fi";

const DataSourceModal = ({ closeModal }) => {
  const {
    userGoogleId,
    fetchDataSourceFiles: fetchFiles,
    allUserDataSourceFiles: files,
    setAllUserDataSourceFiles: setFiles,
  } = DataState();

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingFiles, setDeletingFiles] = useState({});
  const [error, setError] = useState(null);

  const modalRef = useRef(null);

  const handleFileChange = (event) => {
    setSelectedFiles(Array.from(event.target.files));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError("Please select files to upload");
      return;
    }

    try {
      setLoading(true);
      const response = await uploadFilesDataSource(userGoogleId, selectedFiles);
      if (response.error) {
        setError(response.error);
      } else {
        toast.success("Files uploaded successfully");
        setSelectedFiles([]);
        fetchFiles();
      }
    } catch (error) {
      setError("Error uploading files");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fileId) => {
    setDeletingFiles((prev) => ({ ...prev, [fileId]: true }));
    try {
      const response = await deleteFileDataSource(fileId, userGoogleId);
      if (response.error) {
        setError(response.error);
      } else {
        toast.success("File deleted successfully");
        fetchFiles();
      }
    } catch (error) {
      setError("Error deleting file");
    } finally {
      setDeletingFiles((prev) => ({ ...prev, [fileId]: false }));
    }
  };

  const handleDownload = async (fileId) => {
    try {
      const response = await downloadFileDataSource(fileId, userGoogleId);
      if (!response.error) {
        window.open(response.url, "_blank");
      } else {
        setError(response.error);
      }
    } catch (error) {
      setError("Error downloading file");
    }
  };

  const [openPicker] = useDrivePicker();
  const handleOpenPicker = () => {
    openPicker({
      clientId: google_client_id,
      developerKey: google_api_key,
      viewId: "DOCS",
      showUploadView: true,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: true,
      callbackFunction: async (data) => {
        if (data.action === "picked") {
          const fileIds = data.docs.map((doc) => doc.id);
          const response = await uploadFilesFromDriveDataSource(
            userGoogleId,
            fileIds
          );
          if (!response.error) {
            toast.success("Files uploaded from Google Drive");
            fetchFiles();
          }
        }
      },
    });
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const handleClickOutside = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      closeModal();
    }
  };
  // Function to map status to color and icons
  const getFileStatusStyles = (status) => {
    if (status && status.startsWith("failed")) {
      if (status === "failed") {
        // Complete failure
        return {
          color: "text-red-500",
          icon: <FaTimesCircle />,
          text: "Failed",
        };
      } else {
        // Partial failure (still retrying)
        return {
          color: "text-orange-500",
          icon: <FaSpinner className="animate-spin" />,
          text: status,
        };
      }
    }

    switch (status) {
      case "queued":
        return {
          color: "text-[#5652CF]",
          icon: <FaSpinner className="animate-spin" />,
          text: "Queued",
        };
      case "processing":
        return {
          color: "text-yellow-500",
          icon: <FaSpinner className="animate-spin" />,
          text: "Processing",
        };
      case "success":
        return {
          color: "text-green-500",
          icon: <FaCheckCircle />,
          text: "Success",
        };
      default:
        return { color: "text-gray-500", icon: null, text: status };
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const [selectedTab, setSelectedTab] = useState("select_file");
  const handleSelectTab = (tab) => {
    setSelectedTab(tab);
  };
  return (
    <>
      <div
        className="fixed inset-0 z-[110] bg-black bg-opacity-50"
        onClick={closeModal}
      ></div>
      <div className="fixed top-[50%] z-[120] left-[50%] p-8 rounded-lg bg-2 border br-1 shadow-2xl translate-x-[-50%] translate-y-[-50%] flex flex-col w-5/6 sm:max-w-[45rem]  max-h-[min(680px,calc(100vh-2rem))] py-6 text-p-text min-h-96">
        {/* <div className="fixed top-0 left-0 z-[120] p-8 rounded-lg bg-2 border br-1 shadow-2xl flex flex-col w-screen h-screen py-6 text-p-text"> */}
        <div className="flex flex-col gap-6 flex-1 max-h-96">
          {/* <div className="flex flex-col gap-6 flex-1 max-w-4xl w-full mx-auto py-3"> */}
          <header className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-semibold p-text">Data Source</h2>
            </div>
            <button
              onClick={closeModal}
              type="button"
              className="rounded-[3px] text-md font-semibold transition-colors duration-150 ease-in-out disabled:cursor-not-allowed text-xl"
            >
              <IoMdClose />
            </button>
          </header>

          <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto hide-sidebar w-full">
            <div className="w-full">
              <div className="flex p-1 gap-1 bg-3 rounded-full br-1 border w-fit mx-auto">
                <button
                  onClick={() => handleSelectTab("select_file")}
                  class={`flex items-center gap-1 disabled:cursor-not-allowed min-h-7 px-3.5 py-1 rounded-full border-0 text-sm transition-all duration-200 ${selectedTab === "select_file"
                      ? "bg-1 hover:text-s-text text-p-text font-semibold"
                      : "bg-transparent hover:text-s-text text-p-text font-normal"
                    }`}
                >
                  <span>Select File</span>
                </button>
                <button
                  onClick={() => handleSelectTab("upload_file")}
                  class={`flex items-center gap-1 disabled:cursor-not-allowed min-h-7 px-3.5 py-1 rounded-full border-0 text-sm transition-all duration-200 ${selectedTab === "upload_file"
                      ? "bg-1 hover:text-s-text text-p-text font-semibold"
                      : "bg-transparent hover:text-s-text text-p-text font-normal"
                    }`}
                >
                  <span>Upload File</span>
                </button>
              </div>
            </div>

            {selectedTab === "upload_file" && (
              <>
                <div className="grid sm:grid-cols-2 gap-3">
                  <button
                    onClick={handleOpenPicker}
                    className="flex justify-between items-center gap-2 p-2 bg-zinc-800 rounded-md hover:bg-opacity-70 "
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src="https://res.cloudinary.com/dsguoq6ad/image/upload/v1726229007/mentorEducationImages/lhupskidmwtibynwa0no.png"
                        className="h-5 w-5"
                        alt="Google Drive"
                      />
                      <span className="text-sm text-zinc-200">
                        Upload from Google Drive
                      </span>
                    </div>
                    <IoIosArrowForward />
                  </button>
                  <div className="relative flex justify-between items-center gap-2 p-2 bg-zinc-800 rounded-md hover:bg-opacity-70">
                    <div className="flex items-center gap-2">
                      <FaFileUpload className="text-zinc-200 h-5 w-5" />
                      <span className="text-sm text-zinc-200">
                        Upload from Computer
                      </span>
                    </div>
                    <IoIosArrowForward />
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <h3 className="text-base font-semibold text-zinc-100 mb-2">
                    Selected Files
                  </h3>
                  {selectedFiles.length > 0 ? (
                    <>
                      <ul className="space-y-2">
                        {selectedFiles.map((file, index) => (
                          <li
                            key={index}
                            className="flex justify-between items-center bg-zinc-800 hover:bg-opacity-70 rounded-md p-2"
                          >
                            <div className="flex items-center gap-2 max-w-[70%]">
                              <FaFile className="text-zinc-200" />
                              <span
                                className="text-sm truncate hover:whitespace-normal hover:overflow-visible hover:max-w-none transition-all text-zinc-200"
                                title={file.name}
                              >
                                {file.name}
                              </span>
                            </div>
                            <button
                              onClick={() =>
                                setSelectedFiles((prevFiles) =>
                                  prevFiles.filter((_, i) => i !== index)
                                )
                              }
                              className="text-red-400 hover:text-red-300"
                            >
                              <RxCross2 />
                            </button>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={handleUpload}
                        className="bg-zinc-200 text-zinc-800 px-3 py-1 rounded-lg mt-4 flex items-center gap-1 ml-auto"
                        // className={`mt-4 w-full py-2 rounded-md ${
                        //   loading
                        //     ? "bg-zinc-600 cursor-not-allowed"
                        //     : "bg-[#5566FF] hover:bg-[#5652CF]"
                        // }`}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <FaSpinner className="animate-spin mr-2 inline mr-2" />{" "}
                            Uploading
                          </>
                        ) : (
                          "Upload Files"
                        )}
                      </button>
                    </>

                  ) : (
                    <div className="bg-black w-full min-h-10 flex items-center rounded-md justify-center text-zinc-400">
                      No files selected yet.
                    </div>
                  )}

                </div>

                {error && <div className="text-red-500 mt-2">{error}</div>}
                <div className="mt-4">
                  <h3 className="text-base font-semibold text-zinc-100">
                    Files
                  </h3>
                  {files.length === 0 ? (
                    <div className="bg-black w-full min-h-10 flex items-center rounded-md justify-center text-zinc-400">
                      No files uploaded yet.
                    </div>
                  ) : (
                    <ul className="mt-2 space-y-2">
                      {files.map((file) => {
                        const { color, icon } = getFileStatusStyles(file.status);
                        return (
                          <li
                            key={file.file_id}
                            className="flex justify-between gap-2 items-center bg-zinc-800 hover:bg-opacity-70 rounded-md p-2 w-full overflow-hidden"
                          >
                            <div className="flex items-center gap-2 max-w-[70%] min-w-0">
                              <FaFile className="text-zinc-200 flex-shrink-0" />
                              <span
                                onClick={() => handleDownload(file.file_id)}
                                className="text-sm sm:text-sm xs:text-xs truncate cursor-pointer hover:underline text-zinc-200"
                                title={`${file.filename}\nUploaded at: ${file.timestamp}`}
                              >
                                {file.original_filename}
                              </span>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                              {file.status !== "success" && (
                                <div className="flex items-center gap-1 flex-wrap text-xs sm:text-sm">
                                  <span className={`${color}`}>{icon}</span>
                                  <span className={`${color}`}>{file.status}</span>
                                </div>
                              )}

                              <button
                                onClick={() => handleDownload(file.file_id)}
                                className="text-zinc-300 hover:bg-zinc-700 p-1 rounded-lg"
                              >
                                <FiDownload />
                              </button>
                              <button
                                onClick={() => handleDelete(file.file_id)}
                                className="text-zinc-300 hover:bg-zinc-700 p-1 rounded-lg"
                                disabled={deletingFiles[file.file_id]}
                              >
                                {deletingFiles[file.file_id] ? (
                                  <FaSpinner className="animate-spin" />
                                ) : (
                                  <RxCross2 />
                                )}
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </>
            )}
            {selectedTab === "select_file" && (
              <SelectDataSource closeModal={closeModal} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DataSourceModal;